/**
 * Which host is this?
 *
 * One React bundle is served from three kinds of host, and they are NOT two families with
 * localhost folded into one of them:
 *
 *   app         okrhub.hr4india.com, staging.okrhub.hr4india.com — EC2 + nginx, API present
 *   marketing   hr4india.com, www.hr4india.com — S3 + CloudFront, NO backend at all
 *   local dev   localhost, 127.0.0.1 — API present, but nothing is deployed
 *
 * localhost behaves like marketing in one respect (it shows the landing page at `/`) and
 * like an app host in another (its auth pages must work in place, never redirect to the
 * live site). Collapsing the three into a single boolean therefore breaks one caller or the
 * other, which is why this module exports three predicates rather than one.
 *
 * This rule was previously hand-copied in five places in two inconsistent forms. Four of
 * them now import from here; the fifth is the inline analytics gate in index.html, which
 * runs before any bundle loads and so cannot import anything. A test in
 * utils/__tests__/domain.test.ts fails if that copy and isMarketingDomain drift apart.
 */

const hostname = (): string => window.location.hostname;

/**
 * An OKR Hub app host — production or staging. `includes('okrhub.')` matches both, and the
 * trailing dot keeps it from matching the marketing site's own /okrhub page path or a
 * future host literally named `okrhub.com`.
 */
export const isAppDomain = (): boolean => hostname().includes('okrhub.');

/**
 * The marketing site. Matched exactly, not by suffix: `okrhub.hr4india.com` ends with
 * `hr4india.com`, so a suffix test would classify the app as marketing.
 *
 * These hosts are served from S3 + CloudFront with no backend, and CloudFront answers any
 * unknown path — `/api/...` included — with index.html at status 200. Code that would call
 * the API must check this, because the failure is a resolved non-JSON response rather than
 * a connection error.
 */
export const isMarketingDomain = (): boolean =>
  hostname() === 'hr4india.com' || hostname() === 'www.hr4india.com';

/** A developer machine. Not deployed anywhere, and must never be reported as real traffic. */
export const isLocalDev = (): boolean =>
  hostname() === 'localhost' || hostname() === '127.0.0.1' || hostname() === '[::1]';
